import { hierarchyExpression } from './engine';
import type { PolicyType } from './types';
import { EXAMPLE_EXPRESSIONS } from './types';

export interface ExpressionExample {
  id: string;
  type: PolicyType;
  title: string;
  reason: string;
  expression: string;
}

export const EXPRESSION_EXAMPLES: ExpressionExample[] = [
  {
    id: 'row-tenant-region',
    type: 'ROW_LEVEL',
    title: 'Limit rows to caller tenant and region',
    reason: 'Standard isolation so a user only sees their own tenant in their region.',
    expression: EXAMPLE_EXPRESSIONS.ROW_LEVEL,
  },
  {
    id: 'row-hierarchy',
    type: 'ROW_LEVEL',
    title: 'Managers see their reporting chain',
    reason: 'A manager can read their own employee row and every descendant.',
    expression: hierarchyExpression(),
  },
  {
    id: 'row-owner',
    type: 'ROW_LEVEL',
    title: 'Rows owned by the caller',
    reason: 'Account managers only see the customers they own.',
    expression: 'account_manager_id = ${user.employee_id}',
  },
  {
    id: 'row-analyst-junior-salary',
    type: 'ROW_LEVEL',
    title: 'Analysts only see junior salaries',
    reason: 'Analysts are limited to junior pay bands. Other roles keep every row.',
    expression: "NOT ('analyst' IN ${user.roles})\nOR salary < 120000",
  },
  {
    id: 'row-analyst-hide-senior-salary',
    type: 'ROW_LEVEL',
    title: 'Hide senior salary rows from Analysts',
    reason: 'Analysts cannot see senior-band compensation rows.',
    expression: "NOT ('analyst' IN ${user.roles})\nOR salary < 150000",
  },
  {
    id: 'row-salary-cap',
    type: 'ROW_LEVEL',
    title: 'Only rows below a salary cap',
    reason: 'Keep compensation rows whose salary is under 100000.',
    expression: 'salary < 100000',
  },
  {
    id: 'row-salary-usd-band',
    type: 'ROW_LEVEL',
    title: 'USD salaries under a band',
    reason: 'Combine an amount check with another column condition.',
    expression: "salary < 125000\nAND currency = 'USD'",
  },
  {
    id: 'col-own-email',
    type: 'COLUMN_LEVEL',
    title: 'Own email clear, others masked',
    reason: 'Classic when: mask work_email unless the cell is the caller. Pair with Partial · email.',
    expression: 'work_email != ${user.email}',
  },
  {
    id: 'col-payroll-join',
    type: 'COLUMN_LEVEL',
    title: 'Mask payroll except the caller',
    reason: 'Advanced when: mask unless employee_id belongs to the signed-in user via a subquery.',
    expression: 'NOT (employee_id IN (SELECT employee_id FROM employees WHERE work_email = ${user.email}))',
  },
  {
    id: 'col-custom-case',
    type: 'COLUMN_LEVEL',
    title: 'Custom CASE keeps the caller clear',
    reason: 'Protection = Custom · expression writes the mask SQL itself. Do not add a separate when.',
    expression: "CASE WHEN value = ${user.email} THEN CAST(value AS varchar) ELSE regexp_replace(CAST(value AS varchar), '(.).*(@.*)', '$1***$2') END",
  },
  {
    id: 'col-admin-roles',
    type: 'COLUMN_LEVEL',
    title: 'Restrict columns unless Admin',
    reason: 'Mask or deny the column for everyone who is not in the admin role.',
    expression: EXAMPLE_EXPRESSIONS.COLUMN_LEVEL,
  },
  {
    id: 'col-hr-department',
    type: 'COLUMN_LEVEL',
    title: 'Hide columns unless HR',
    reason: 'Payroll and salary columns stay hidden unless the caller is in HR.',
    expression: "${user.department} != 'HR'",
  },
  {
    id: 'col-mask-senior-salary',
    type: 'COLUMN_LEVEL',
    title: 'Mask senior salaries unless HR or Admin',
    reason: 'Hide the salary column on senior-band rows for everyone except HR managers and admins.',
    expression: "salary >= 150000\nAND NOT ('hr_manager' IN ${user.roles} OR 'admin' IN ${user.roles})",
  },
  {
    id: 'col-external-group',
    type: 'COLUMN_LEVEL',
    title: 'Deny External group column access',
    reason: 'External consumers should not see PII columns.',
    expression: "'external' IN ${user.groups}\nOR 'External' IN ${user.groups}",
  },
  {
    id: 'access-finance-select',
    type: 'ACCESS_CONTROL',
    title: 'Finance may only Read',
    reason: 'Allow the Finance group to SELECT, and only SELECT.',
    expression: EXAMPLE_EXPRESSIONS.ACCESS_CONTROL,
  },
  {
    id: 'access-deny-external-write',
    type: 'ACCESS_CONTROL',
    title: 'Deny External writes',
    reason: 'Block INSERT, UPDATE, and DELETE from the External group.',
    expression: "'External' IN ${user.groups}\nAND ${request.operation} IN ('INSERT', 'UPDATE', 'DELETE')",
  },
  {
    id: 'access-contractor-read',
    type: 'ACCESS_CONTROL',
    title: 'Contractors are read-only',
    reason: 'Contractors can query, but any write operation fails this rule.',
    expression: "'Contractors' IN ${user.groups}\nAND ${request.operation} = 'SELECT'",
  },
  {
    id: 'sens-pii-external',
    type: 'SENSITIVE_DATA',
    title: 'Mask PII and Restricted for External',
    reason: 'Apply the sensitive-data mask when the asset is classified and the caller is External.',
    expression: EXAMPLE_EXPRESSIONS.SENSITIVE_DATA,
  },
  {
    id: 'sens-pci-finance',
    type: 'SENSITIVE_DATA',
    title: 'PCI except Finance',
    reason: 'Payment fields stay masked unless the caller is in Finance.',
    expression: "${resource.classification} = 'PCI'\nAND NOT ('Finance' IN ${user.groups})",
  },
  {
    id: 'sens-restricted-clearance',
    type: 'SENSITIVE_DATA',
    title: 'Restricted unless high clearance',
    reason: 'Restricted columns are visible only to users with high clearance.',
    expression: "${resource.classification} = 'Restricted'\nAND ${user.attributes.clearance} != 'high'",
  },
];

export function examplesForType(type: PolicyType): ExpressionExample[] {
  const own = EXPRESSION_EXAMPLES.filter((item) => item.type === type);
  const rest = EXPRESSION_EXAMPLES.filter((item) => item.type !== type);
  return [...own, ...rest];
}
